import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Tile,
  Form,
  PasswordInput,
  Button,
  Grid,
  Column,
  Stack,
  Heading,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import { Security, CheckmarkFilled, ArrowLeft, WarningAltFilled } from '@carbon/icons-react';

import { authService } from '../../services/authService';
import styles from './ResetPasswordPage.module.scss';

interface ResetPasswordPageProps {}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'reset' | 'success' | 'error'>('reset');
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    // Validate token presence
    if (!token) {
      setTokenValid(false);
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  // Password strength calculation
  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    const errors: string[] = [];

    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    } else {
      strength += 25;
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    } else if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    } else {
      strength += 25;
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one digit');
    } else {
      strength += 25;
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    } else {
      strength += 25;
    }

    setPasswordStrength(strength);
    setPasswordErrors(errors);
    return strength === 100;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !tokenValid) {
      setError('Invalid reset token. Please request a new password reset.');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const isStrong = calculatePasswordStrength(newPassword);
    if (!isStrong) {
      setError('Password does not meet security requirements. See requirements below.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Call the password reset service
      await authService.resetPassword(token, newPassword);

      setStep('success');
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(
        error.message || 'Failed to reset password. The link may have expired. Please request a new password reset.'
      );
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength === 0) return 'Very Weak';
    if (passwordStrength === 25) return 'Weak';
    if (passwordStrength === 50) return 'Fair';
    if (passwordStrength === 75) return 'Good';
    return 'Strong';
  };

  const getStrengthClasses = () => {
    if (passwordStrength <= 25)
      return {
        border: 'error-border',
        text: 'error-text',
        bg: 'error-bg',
      };
    if (passwordStrength === 50)
      return {
        border: 'warning-border',
        text: 'warning-text',
        bg: 'warning-bg',
      };
    if (passwordStrength === 75)
      return {
        border: 'info-border',
        text: 'info-text',
        bg: 'info-bg',
      };
    return {
      border: 'success-border',
      text: 'success-text',
      bg: 'success-bg',
    };
  };

  const renderResetStep = () => {
    const strengthClasses = getStrengthClasses();

    return (
      <Form onSubmit={handleSubmit}>
        <Stack gap={5}>
          <div className={styles['text-center']}>
            <div className={`${styles['icon-circle']} ${tokenValid ? styles.brand : styles.error}`}>
              {tokenValid ? (
                <Security size={24} className={styles['icon-on-color']} />
              ) : (
                <WarningAltFilled size={24} className={styles['icon-on-color']} />
              )}
            </div>
            <Heading className={styles.heading}>{tokenValid ? 'Create New Password' : 'Invalid Reset Link'}</Heading>
            <p className={styles.description}>
              {tokenValid
                ? 'Choose a strong password that meets all security requirements below.'
                : 'This password reset link is invalid or has expired.'}
            </p>
          </div>

          {error && <InlineNotification kind="error" title="Error" subtitle={error} lowContrast hideCloseButton />}

          {!tokenValid && (
            <InlineNotification
              kind="warning"
              title="Link Expired or Invalid"
              subtitle="Password reset links expire after 1 hour for security reasons. Please request a new password reset."
              lowContrast
              hideCloseButton
            />
          )}

          {tokenValid && (
            <>
              <PasswordInput
                id="new_password"
                name="new_password"
                labelText="New Password"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={e => {
                  setNewPassword(e.target.value);
                  calculatePasswordStrength(e.target.value);
                }}
                required
                disabled={loading}
                autoComplete="new-password"
                autoFocus
                helperText="Minimum 12 characters with uppercase, lowercase, digit, and special character"
              />

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className={`${styles['password-strength-container']} ${styles[strengthClasses.border]}`}>
                  <div className={styles['strength-header']}>
                    <span className={styles['strength-label']}>Password Strength: {getPasswordStrengthLabel()}</span>
                    <span className={`${styles['strength-percentage']} ${styles[strengthClasses.text]}`}>
                      {passwordStrength}%
                    </span>
                  </div>
                  <div className={styles['strength-bar-background']}>
                    <div
                      className={`${styles['strength-bar-fill']} ${styles[strengthClasses.bg]}`}
                      style={{ width: `${passwordStrength}%` }}
                    />
                  </div>
                  {passwordErrors.length > 0 && (
                    <ul className={styles['password-errors']}>
                      {passwordErrors.map((err, idx) => (
                        <li key={idx} className={styles['error-item']}>
                          {err}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <PasswordInput
                id="confirm_password"
                name="confirm_password"
                labelText="Confirm New Password"
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
                invalid={confirmPassword.length > 0 && newPassword !== confirmPassword}
                invalidText="Passwords do not match"
              />

              <Button
                type="submit"
                size="lg"
                disabled={loading || !newPassword || !confirmPassword || passwordStrength < 100}
                className={styles['full-width-button']}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </>
          )}

          <div className={styles['text-center']}>
            <Button
              kind="ghost"
              size="sm"
              onClick={handleBackToLogin}
              disabled={loading}
              renderIcon={() => <ArrowLeft size={16} />}
            >
              Back to Sign In
            </Button>
          </div>
        </Stack>
      </Form>
    );
  };

  const renderSuccessStep = () => (
    <Stack gap={5}>
      <div className={styles['text-center']}>
        <div className={`${styles['icon-circle']} ${styles.success}`}>
          <CheckmarkFilled size={24} className={styles['icon-on-color']} />
        </div>
        <Heading className={styles.heading}>Password Reset Successful</Heading>
        <p className={styles.description}>
          Your password has been successfully reset. You can now sign in with your new password.
        </p>
      </div>

      <InlineNotification
        kind="success"
        title="Password Updated"
        subtitle="All active sessions have been terminated for security. Please sign in again with your new password."
        lowContrast
        hideCloseButton
      />

      <div className={styles['info-box']}>
        <p className={styles['info-text']}>
          <span className={styles['info-text-strong']}>Security Notice:</span>
          <br />A confirmation email has been sent to your registered email address. If you didn't request this password
          change, please contact support immediately.
        </p>
      </div>

      <Button
        kind="primary"
        size="lg"
        onClick={handleBackToLogin}
        className={styles['full-width-button']}
        renderIcon={() => <ArrowLeft size={16} />}
      >
        Go to Sign In
      </Button>
    </Stack>
  );

  return (
    <div className={styles['page-container']}>
      <Grid narrow>
        <Column sm={4} md={6} lg={6} xlg={4} max={4}>
          <Tile className={styles['form-tile']}>
            {/* Progress Indicator */}
            <div className={styles['progress-container']}>
              <ProgressIndicator currentIndex={step === 'reset' ? 0 : 1} spaceEqually>
                <ProgressStep label="Reset Password" description="Enter new password" complete={step !== 'reset'} />
                <ProgressStep label="Complete" description="Password updated" complete={step === 'success'} />
              </ProgressIndicator>
            </div>

            {/* Content based on current step */}
            {step === 'reset' && renderResetStep()}
            {step === 'success' && renderSuccessStep()}
            {step === 'error' && renderResetStep()}

            {/* Footer Section */}
            <div className={styles.footer}>
              <p className={styles['footer-text']}>
                🔒 Secure password reset • ✅ All sessions invalidated
                <br />
                📧 Confirmation email sent • 🛡️ FERPA & COPPA Compliant
              </p>
            </div>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default ResetPasswordPage;
